import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, Row, Col, Tag } from 'antd';
import { TrophyOutlined } from '@ant-design/icons';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import * as echarts from 'echarts/core';
import { PieChart, BarChart, LineChart } from 'echarts/charts';
import { GridComponent, TooltipComponent, LegendComponent } from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';

echarts.use([PieChart, BarChart, LineChart, GridComponent, TooltipComponent, LegendComponent, CanvasRenderer]);

const COLORS = {
  success: '#52C41A',
  warning: '#FAAD14',
  danger: '#FF4D4F',
  neutral: '#8C8C8C',
  blue: '#1890ff',
  orange: '#ff7a45',
};

const MEDAL_COLORS = ['#FFD700', '#C0C0C0', '#CD7F32'];

/**
 * Module 3: Workforce & Productivity
 * Donut (online/offline) + Dual-axis trend + Idle time bar + Leaderboard
 */
const WorkforcePanel = ({ data }) => {
  const { t } = useTranslation();
  if (!data) return null;

  // Donut chart - Online/Idle/Offline capacity
  const donutOption = {
    tooltip: { trigger: 'item', formatter: '{b}: {c} ({d}%)' },
    legend: { bottom: 0, data: [t('dashboard.workforce.online'), t('dashboard.workforce.idle'), t('dashboard.workforce.offline')], textStyle: { fontSize: 12 } },
    series: [{
      type: 'pie',
      radius: ['50%', '72%'],
      center: ['50%', '42%'],
      avoidLabelOverlap: false,
      label: {
        show: true,
        position: 'center',
        formatter: () => `${data.onlinePickers}\n${t('dashboard.workforce.online')}`,
        fontSize: 18,
        fontWeight: 'bold',
        lineHeight: 22,
        color: COLORS.success,
      },
      emphasis: {
        label: {
          show: true,
          formatter: '{b}\n{c}',
          fontSize: 16,
          fontWeight: 'bold',
        },
      },
      data: [
        { value: data.onlinePickers - (data.idlePickers || 0), name: t('dashboard.workforce.online'), itemStyle: { color: COLORS.success } },
        { value: data.idlePickers || 0, name: t('dashboard.workforce.idle'), itemStyle: { color: COLORS.warning } },
        { value: data.offlinePickers, name: t('dashboard.workforce.offline'), itemStyle: { color: '#d9d9d9' } },
      ],
    }],
  };

  // Dual-axis trend: Bar(orders) + Line(Picking Duration Per Unit)
  const trendData = data.weeklyOrderTrend || [];
  const comboOption = {
    tooltip: { trigger: 'axis' },
    legend: { data: [t('dashboard.workforce.ordersLabel'), t('dashboard.workforce.pickingDurationPerUnit')], bottom: 0 },
    grid: { left: 55, right: 55, top: 20, bottom: 50 },
    xAxis: {
      type: 'category',
      data: trendData.map((d) => d.day),
    },
    yAxis: [
      { type: 'value', name: t('dashboard.workforce.ordersLabel'), position: 'left', splitLine: { lineStyle: { type: 'dashed' } } },
      { type: 'value', name: t('dashboard.workforce.minUnitAxis'), position: 'right', splitLine: { show: false } },
    ],
    series: [
      {
        name: t('dashboard.workforce.ordersLabel'),
        type: 'bar',
        data: trendData.map((d) => d.orders),
        itemStyle: { color: COLORS.blue, borderRadius: [4, 4, 0, 0] },
        barWidth: 20,
      },
      {
        name: t('dashboard.workforce.pickingDurationPerUnit'),
        type: 'line',
        yAxisIndex: 1,
        data: trendData.map((d) => d.pickingDurationPerUnit),
        itemStyle: { color: COLORS.orange },
        lineStyle: { width: 2.5 },
        symbol: 'circle',
        symbolSize: 7,
      },
    ],
  };

  // Pie chart - Online Time Split (avg online time vs avg idle time)
  const timeSplitPieOption = {
    tooltip: { trigger: 'item', formatter: '{b}: {c} min ({d}%)' },
    legend: { bottom: 0, data: [t('dashboard.workforce.avgOnlineTime'), t('dashboard.workforce.avgIdleTime')], textStyle: { fontSize: 11 } },
    series: [{
      type: 'pie',
      radius: ['45%', '68%'],
      center: ['50%', '42%'],
      avoidLabelOverlap: false,
      label: {
        show: true,
        position: 'center',
        formatter: () => `${data.avgOnlineTime + data.avgIdleTime}\n${t('dashboard.total')}`,
        fontSize: 14,
        fontWeight: 'bold',
        lineHeight: 18,
        color: '#666',
      },
      emphasis: {
        label: {
          show: true,
          formatter: '{b}\n{c} min',
          fontSize: 13,
          fontWeight: 'bold',
        },
      },
      data: [
        { value: data.avgOnlineTime, name: t('dashboard.workforce.avgOnlineTime'), itemStyle: { color: COLORS.success } },
        { value: data.avgIdleTime, name: t('dashboard.workforce.avgIdleTime'), itemStyle: { color: COLORS.warning } },
      ],
    }],
  };

  const topPickers = data.topPickers || [];

  return (
    <Card title={t('dashboard.workforce.title')} style={{ height: '100%' }} styles={{ body: { padding: 16 } }}>
      <Row gutter={16}>
        {/* Left: Donut + summary metrics + Time Split pie */}
        <Col span={6}>
          <div style={{ fontWeight: 600, marginBottom: 8, textAlign: 'center', fontSize: 13 }}>{t('dashboard.workforce.pickerOnlineCapacity')}</div>
          <ReactEChartsCore echarts={echarts} option={donutOption} style={{ height: 150 }} opts={{ renderer: 'canvas' }} />
          {/* Online Time Split Pie Chart */}
          <div style={{ fontWeight: 600, marginBottom: 4, marginTop: 12, textAlign: 'center', fontSize: 12, color: '#666' }}>{t('dashboard.workforce.onlineTimeSplit')}</div>
          <ReactEChartsCore echarts={echarts} option={timeSplitPieOption} style={{ height: 130 }} opts={{ renderer: 'canvas' }} />
        </Col>

        {/* Right: Trend chart on left, Leaderboard on right */}
        <Col span={18}>
          <Row gutter={16}>
            {/* Left: Trend chart + KPI row */}
            <Col span={14}>
              <div style={{ fontWeight: 600, marginBottom: 8, textAlign: 'center', fontSize: 13 }}>{t('dashboard.workforce.weeklyTrend')}</div>
              <ReactEChartsCore echarts={echarts} option={comboOption} style={{ height: 230 }} opts={{ renderer: 'canvas' }} />
              {/* KPI summary row - two cards side by side */}
              <div style={{ display: 'flex', gap: 12, marginTop: 20, marginBottom: 8 }}>
                <div style={{ flex: 1, background: '#e6f7ff', borderRadius: 6, padding: '8px 12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: 12, color: '#666' }}>{t('dashboard.workforce.ordersPerPersonHr')}</span>
                  <span style={{ fontSize: 20, fontWeight: 'bold', color: COLORS.blue }}>{data.ordersPerPersonPerHour}</span>
                </div>
                <div style={{ flex: 1, background: '#fff7e6', borderRadius: 6, padding: '8px 12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: 12, color: '#666' }}>{t('dashboard.workforce.minPerOrder')}</span>
                  <span style={{ fontSize: 20, fontWeight: 'bold', color: COLORS.warning }}>{data.timePerOrder}</span>
                </div>
              </div>
            </Col>

            {/* Right: Leaderboard */}
            <Col span={10}>
              <div style={{ fontWeight: 600, marginBottom: 8, textAlign: 'center', fontSize: 13 }}>
                <TrophyOutlined style={{ color: '#FFD700', marginRight: 4 }} />
                {t('dashboard.workforce.pickerLeaderboard')}
              </div>
              <div style={{ maxHeight: 290, overflow: 'auto' }}>
                {topPickers.map((picker, idx) => (
                  <div
                    key={picker.name}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '6px 8px',
                      borderRadius: 6,
                      marginBottom: 4,
                      background: idx < 3 ? `${MEDAL_COLORS[idx]}15` : '#fafafa',
                      border: idx < 3 ? `1px solid ${MEDAL_COLORS[idx]}40` : '1px solid #f0f0f0',
                    }}
                  >
                    {/* Rank badge */}
                    <div
                      style={{
                        width: 26,
                        height: 26,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: idx < 3 ? 14 : 12,
                        fontWeight: 'bold',
                        background: idx < 3 ? MEDAL_COLORS[idx] : '#e8e8e8',
                        color: idx < 3 ? '#fff' : '#666',
                        marginRight: 8,
                        flexShrink: 0,
                      }}
                    >
                      {idx + 1}
                    </div>
                    {/* Name */}
                    <div style={{ flex: 1, fontSize: 13, fontWeight: idx < 3 ? 600 : 400 }}>
                      {picker.name}
                    </div>
                    {/* Metrics */}
                    <Tag color="blue" style={{ marginRight: 4 }}>{picker.orders} {t('dashboard.orders')}</Tag>
                    <Tag color="green" style={{ marginRight: 4 }}>{picker.items} {t('dashboard.items')}</Tag>
                    <Tag color="orange">{picker.pickingDurationPerUnit} {t('dashboard.workforce.minUnit')}</Tag>
                  </div>
                ))}
              </div>
            </Col>
          </Row>
        </Col>
      </Row>
    </Card>
  );
};

export default WorkforcePanel;
